import {LauncherServiceBase} from '../launcher-service-base';
import {ILauncherServiceFactory} from '../launcher-service-factory.interface';
import {makeSdkConfigurationArea} from './areas-definitions/sdk/sdk-configurations-area';
import {ConfigurationArea} from './configuration-area';
import {extractConfigurationFields} from './areas-definitions/make-configuration-area';
import {makeGamesSettingsArea} from './areas-definitions/games/games-settings-area';
import {makeOrbitConfigurationArea} from './areas-definitions/orbit/orbit-configurations-area';
import {action, observable} from 'mobx';
import {makeSegmentsConfigurationArea} from './areas-definitions/segments/segments-configuration-area';
import {IConfigFieldDefinition} from './fields-definitions/config-field-definition-base';
import {ROOT_CONFIGURATION_KEYS} from './root-configuration-keys';
import {BooleanFieldDefinition} from './fields-definitions/boolean-field-definition';
import {makeLocalStorageOverridesArea} from './areas-definitions/local-storage-overrides/local-storage-overrides-area';
import {makeConfigurationOverridesArea} from "./areas-definitions/configuration-overrides/configuration-overrides-area";

const B2C_SEGMENTS_TITLE = 'B2C SEGMENTS';

// this service deliberately doesn't implement an interface in order to make it easy exposing strongly typed configurations areas
export class ConfigurationsAreasService extends LauncherServiceBase {

    constructor(services: ILauncherServiceFactory) {
        super(services);

        this.sdk = makeSdkConfigurationArea(this.services);
        this.configurationOverrides = makeConfigurationOverridesArea(this.services);
        this.orbit = makeOrbitConfigurationArea(this.services);
        this.gamesSettings = makeGamesSettingsArea(this.services);
        this.segments = makeSegmentsConfigurationArea(this.services);

        this._setAreas([
            new ConfigurationArea('SDK CONFIGURATIONS', extractConfigurationFields(this.sdk)),
            new ConfigurationArea('ORBIT CONFIGURATIONS', extractConfigurationFields(this.orbit)),
            new ConfigurationArea('GAMES SETTINGS', extractConfigurationFields(this.gamesSettings)),
            new ConfigurationArea('LOCAL STORAGE OVERRIDES', extractConfigurationFields(makeLocalStorageOverridesArea(this.services))),
            new ConfigurationArea('CONFIGURATION OVERRIDES', extractConfigurationFields(this.configurationOverrides)),
            new ConfigurationArea(B2C_SEGMENTS_TITLE, extractConfigurationFields(this.segments))
        ]);


        this.reaction(() => this.orbit.cmsSource.getValue(),
            (cmsSource) => {
                this._updateSegmentsArea(cmsSource);
            },
            {
                fireImmediately: true
            });

    }

    readonly sdk: ReturnType<typeof makeSdkConfigurationArea>;
    readonly configurationOverrides: ReturnType<typeof makeConfigurationOverridesArea>;
    readonly orbit: ReturnType<typeof makeOrbitConfigurationArea>;
    readonly gamesSettings: ReturnType<typeof makeGamesSettingsArea>;
    readonly segments: ReturnType<typeof makeSegmentsConfigurationArea>;


    @observable
    areas: ConfigurationArea[] = [];

    @action.bound
    private _setAreas(areas: ConfigurationArea[]): void {
        this.areas = areas;
    }


    private async _updateSegmentsArea(cmsSource: string): Promise<void> {

        let dynamicSegmentsFields = await this._readSegmentsFromApi(cmsSource);

        let allSegmentsFields: {[key: string]: IConfigFieldDefinition} = {};

        extractConfigurationFields(this.segments).forEach(hardCodedField => allSegmentsFields[hardCodedField.title] = hardCodedField);

        dynamicSegmentsFields.forEach(dynamicField => {
           if(!allSegmentsFields[dynamicField.title]) {
               allSegmentsFields[dynamicField.title] = dynamicField;
           }
        });

        let segmentsAreaIndex =  this.areas.findIndex(area => area.title === B2C_SEGMENTS_TITLE);
        this.areas[segmentsAreaIndex] = new ConfigurationArea(B2C_SEGMENTS_TITLE, Object.values(allSegmentsFields));
    }

    private async _readSegmentsFromApi(cmsSource: string): Promise<IConfigFieldDefinition[]> {
        cmsSource = cmsSource || 'https://www.888casino.com';

        let segments = await this.services.http.get(`${cmsSource}/api/segments`);

        let segmentationFields: IConfigFieldDefinition[] =[];
        Object.keys(segments).forEach(segmentName => {
            let fieldDefintion = new BooleanFieldDefinition(segmentName);
            fieldDefintion.injectDependencies({
                services: this.services,
                configurationKey: `${ROOT_CONFIGURATION_KEYS.webDataSegments}.${segmentName}`
            });
            segmentationFields.push(fieldDefintion);
        });

        return segmentationFields;
    }

    get allFields(): IConfigFieldDefinition[] {
        let result: IConfigFieldDefinition[] = [];

        this.areas.forEach(area => {
           result = [...result, ...area.fields];
        });

        return result;
    }


}